// Web platform adapter for the shared NotificationService. Wires the decision
// core (packages/ui) to browser I/O: in-app banner (bannerStore), OS
// notification (Notification API), chimes (MessageSoundManager), and the
// unread badge (navigator.setAppBadge + document.title). Also exposes the
// notification-permission request used by the Settings pane.

import { liveQuery } from 'dexie';

import {
  createNotificationService,
  type NotificationPreferences,
  type NotificationService,
  type OsNotificationPresentation,
  pushBanner,
} from '@ping/ui';

import { log } from '@core/log/log';
import { db } from '@core/persistence/db';
import { retrieveIdentity } from '@core/persistence/identity';
import { preferencesStore } from '@core/preferences/PreferencesStore';

import { getActiveNotificationConversation } from './activeConversation';
import { messageSoundManager } from './MessageSoundManager';

// ── ambient state ─────────────────────────────────────────────────────────

const mutedIds = new Set<string>();
// conversationId → explicit notification level (Chat Info → Notifications).
// Absent = 'all'. Kept in sync via the same liveQuery as `mutedIds`.
const levelById = new Map<string, 'all' | 'mentions' | 'off'>();
let ownDeviceIdHex: string | null = null;
const baseTitle = typeof document !== 'undefined' ? document.title || 'Ping' : 'Ping';

function b64urlToHex(b64url: string): string {
  try {
    const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64);
    let hex = '';
    for (let i = 0; i < bin.length; i++) {
      hex += bin.charCodeAt(i).toString(16).padStart(2, '0');
    }
    return hex;
  } catch {
    return '';
  }
}

/** Hydrate the muted-conversation set + own-device id. Idempotent; called once
 *  from the AppShell notification host. */
export function initWebNotifications(): () => void {
  void retrieveIdentity().then((identity) => {
    if (identity?.deviceId) ownDeviceIdHex = b64urlToHex(identity.deviceId);
  });

  // Background/hidden-tab OS notifications need browser permission, which stays
  // 'default' unless we ask. The Settings toggle defaults ON, so users never
  // toggle it and are never prompted → they silently get no background alerts.
  // Request ONCE on the first real user gesture (browsers require a gesture and
  // block auto-prompts), only when OS notifications are enabled in prefs.
  const maybeRequestPermission = (): void => {
    window.removeEventListener('pointerdown', maybeRequestPermission);
    window.removeEventListener('keydown', maybeRequestPermission);
    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default' &&
      preferencesStore.getState().osNotificationsEnabled
    ) {
      void requestOsNotificationPermission();
    }
  };
  if (typeof window !== 'undefined' && typeof Notification !== 'undefined') {
    window.addEventListener('pointerdown', maybeRequestPermission, { once: true });
    window.addEventListener('keydown', maybeRequestPermission, { once: true });
  }

  const sub = liveQuery(() =>
    db.conversations.filter((c) => c.isMuted === true).toArray(),
  ).subscribe({
    next: (rows) => {
      mutedIds.clear();
      levelById.clear();
      for (const r of rows) {
        mutedIds.add(r.id);
        if (r.notificationLevel !== undefined) levelById.set(r.id, r.notificationLevel);
      }
    },
    error: (err: unknown) => {
      log.warn({ event: 'notifications.muteSync.failed', error: (err as Error).message });
    },
  });
  return () => {
    sub.unsubscribe();
    if (typeof window !== 'undefined') {
      window.removeEventListener('pointerdown', maybeRequestPermission);
      window.removeEventListener('keydown', maybeRequestPermission);
    }
  };
}

function mapPreferences(): NotificationPreferences {
  const p = preferencesStore.getState();
  return {
    notificationsEnabled: p.notificationsEnabled,
    osNotificationsEnabled: p.osNotificationsEnabled,
    inAppSounds: p.inAppSounds,
    notificationPreview: p.notificationPreview,
    notifyOnlyWhenUnfocused: p.notifyOnlyWhenUnfocused,
    dndEnabled: p.dndEnabled,
    dndStart: p.dndStart,
    dndEnd: p.dndEnd,
  };
}

// The Ping tab is VISIBLE on screen (not a hidden background tab). Drives the
// in-app banner — a visible tab always shows it.
function isTabVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState === 'visible';
}

// The browser window is the OS-focused window. When false (another app/window
// on top), we ALSO raise an OS notification so the alert surfaces over it.
function isWindowFocused(): boolean {
  if (typeof document === 'undefined') return true;
  if (typeof document.hasFocus !== 'function') return true;
  return document.visibilityState === 'visible' && document.hasFocus();
}

// ── badge ───────────────────────────────────────────────────────────────────

interface BadgeApi {
  setAppBadge?: (n?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

export async function refreshBadge(): Promise<void> {
  try {
    const convos = await db.conversations.toArray();
    let total = 0;
    for (const c of convos) {
      const n = (c as { unreadCount?: number }).unreadCount;
      if (typeof n === 'number' && n > 0) total += n;
    }
    const nav = navigator as unknown as BadgeApi;
    if (total > 0) {
      void nav.setAppBadge?.(total).catch(() => undefined);
      if (typeof document !== 'undefined') document.title = `(${total}) ${baseTitle}`;
    } else {
      void nav.clearAppBadge?.().catch(() => undefined);
      if (typeof document !== 'undefined') document.title = baseTitle;
    }
  } catch {
    /* best-effort */
  }
}

// ── OS notifications ─────────────────────────────────────────────────────────

/** Ask the browser for notification permission. Returns the resulting state.
 *  Safe to call from a user gesture (the Settings toggle). */
export async function requestOsNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

export function osNotificationsGranted(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

/** Current browser permission state for the Settings UI: 'granted' | 'denied'
 *  | 'default' | 'unsupported'. 'denied' is terminal — the browser will NOT
 *  re-prompt, so the UI must tell the user to unblock it in site settings. */
export function osNotificationPermission(): 'granted' | 'denied' | 'default' | 'unsupported' {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

function presentOsNotification(
  n: OsNotificationPresentation,
  onOpenConversation: (id: string) => void,
): void {
  // When OS permission isn't granted (or the API is unavailable), fall back to
  // the in-app banner so the alert isn't silently dropped. The banner is only
  // visible if the tab is on-screen, but that still covers the common
  // "visible-but-not-focused window" case; a fully hidden tab genuinely needs
  // OS permission (prompt via Settings → Notifications) or web-push.
  if (!osNotificationsGranted()) {
    pushBanner({
      id: `os-fallback-${n.tag}-${Date.now()}`,
      title: n.title,
      body: n.body,
      isMention: false,
      ...(n.conversationId !== undefined ? { conversationId: n.conversationId } : {}),
    });
    return;
  }
  try {
    const notification = new Notification(n.title, {
      body: n.body,
      tag: n.tag,
      // Ping logomark, so the alert is branded (not a generic globe). Install
      // Ping as an app (PWA manifest) to also replace the "Google Chrome"
      // attribution + system chrome with Ping's name/icon — the visual frame
      // itself is owned by the OS and can't be themed from script.
      icon: '/favicon.svg',
      badge: '/favicon.svg',
      // We play our own chime (MessageSoundManager); silence the OS sound so
      // the user doesn't hear a double-beep.
      silent: true,
    });
    notification.onclick = () => {
      try {
        window.focus();
      } catch {
        /* ignore */
      }
      if (n.conversationId) onOpenConversation(n.conversationId);
      notification.close();
    };
  } catch (err) {
    log.warn({ event: 'notifications.os.failed', error: (err as Error).message });
  }
}

// ── service factory ──────────────────────────────────────────────────────────

export function createWebNotificationService(
  onOpenConversation: (id: string) => void,
): NotificationService {
  return createNotificationService({
    getPreferences: mapPreferences,
    getActiveConversationId: getActiveNotificationConversation,
    getTabVisible: isTabVisible,
    getWindowFocused: isWindowFocused,
    isConversationMuted: (id) => mutedIds.has(id),
    getConversationNotificationLevel: (id) => levelById.get(id),
    isOwnSender: (senderId) => ownDeviceIdHex !== null && senderId === ownDeviceIdHex,
    presentBanner: pushBanner,
    presentOsNotification: (n) => presentOsNotification(n, onOpenConversation),
    playSound: (s) => messageSoundManager.play(s),
    refreshBadge: () => void refreshBadge(),
  });
}
